import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, TextField,
  MenuItem, Alert, CircularProgress, InputAdornment, IconButton, Typography,
} from '@mui/material';
import { Replay, Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { adminRecordRefund } from 'api/wallet';
import EvidenceUploadButton from 'components/EvidenceUploadButton';
import { sectionLabelSx, fieldSx } from 'components/bento/BentoFormControls';

const CURRENCIES = ['THB', 'LAK', 'USD'];
const CURRENCY_SYMBOLS = { THB: '฿', LAK: '₭', USD: '$' };
const EMPTY_FORM = { amount: '', currency: 'THB', reason: '' };

// Refund dialog for a customer whose wallet account is already known (reached
// from the account transactions screen) — mirrors WalletTopupDialog. Credits the
// wallet directly (no branch cash drawer involved), so unlike cash payments this
// doesn't collect a station — that only applies when the customer later withdraws
// as physical cash.
const WalletRefundDialog = ({ open, onClose, account, onSuccess }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setFile(null);
    setError('');
  }, [open, account]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async () => {
    if (!account || !form.amount || !form.reason.trim()) {
      setError(t('wallet.admin.refundAmountReasonRequired'));
      return;
    }
    if (!file) {
      setError(t('wallet.admin.evidenceRequired'));
      return;
    }
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      fd.append('amount', form.amount);
      fd.append('currency', form.currency);
      fd.append('reason', form.reason);
      fd.append('evidencePhoto', file);
      await adminRecordRefund(account.id, fd);
      onSuccess?.({ amount: parseFloat(form.amount), currency: form.currency });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !loading && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '20px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontWeight: 800 }}>
        <Box>
          {t('wallet.admin.refundTab')}
          {account && (
            <Typography variant="caption" color="text.secondary" display="block">
              {account.id} — {account.customer?.name}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} disabled={loading}><Close fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        <Alert severity="warning" sx={{ mb: 2 }}>{t('wallet.admin.refundNote')}</Alert>

        <Box sx={sectionLabelSx}>{t('wallet.admin.sections.purchaseDetails')}</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr' }, gap: '16px', mb: '16px' }}>
          <TextField
            label={t('wallet.amount')}
            type="number"
            value={form.amount}
            onChange={set('amount')}
            inputProps={{ min: 0.01, step: 0.01 }}
            sx={fieldSx}
            InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOLS[form.currency]}</InputAdornment> }}
          />
          <TextField select label={t('wallet.currency')} value={form.currency} onChange={set('currency')} sx={fieldSx}>
            {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Box>

        <TextField
          fullWidth
          required
          multiline
          rows={2}
          label={t('wallet.admin.reason')}
          value={form.reason}
          onChange={set('reason')}
          placeholder="Explain why this refund is being issued..."
          sx={{ ...fieldSx, mb: '16px' }}
        />

        <Box sx={sectionLabelSx}>{t('wallet.admin.sections.evidence')}</Box>
        <EvidenceUploadButton
          file={file}
          onChange={(e) => setFile(e.target.files[0] || null)}
          label={t('wallet.admin.evidenceRequired')}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          disabled={loading || !form.amount || !form.reason || !file}
          startIcon={loading ? <CircularProgress size={18} /> : <Replay fontSize="small" />}
        >
          {loading ? t('wallet.admin.recordingRefund') : t('wallet.admin.refundTab')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletRefundDialog;
